import {
  IAuthorWithEmail,
  IDependentFundingGenericValue,
  IDependentValue,
  IDependentValueWithText,
  ISubmissionFileSet,
  ISubmissionPreference
} from '@publishing-engine/domain/models';
import { IFormSchema } from '@publishing-engine/domain/models/form-schema';
import { omit } from 'lodash';
import { createContext, ReactNode, useContext, useState } from 'react';

type ISubmissionContext = {
  submissionContext: {
    submissionContextId: string;
    articleWorkflowId: string;
    schema: IFormSchema;
    documentType: string;
    schemaId: string;
    articleTypeCategoryId: string;
    organizationId: string;
  };
  showDiscardChangesModal?: boolean;
};

export type IKeywordsFormData = {
  keywords: { text: string }[];
};
export type IEnglishKeywordsFormData = {
  englishKeywords: { text: string }[];
};

export type SubmissionAuthorContribution = {
  contribution: string;
  degree: string;
};

export type ISubmissionAuthor = IAuthorWithEmail & {
  contributions?: Array<SubmissionAuthorContribution>;
};

type ISubmissionMetadata = {
  title: string;
  englishTitle: string;
  abstract: string;
  englishAbstract: string;
  keywords: string[];
  englishKeywords?: string[];
  authors: ISubmissionAuthor[];
  ethicsStatement?: IDependentValue<string>;
  clinicalTrials?: IDependentValue<string>;
  noveltyStatement?: string;
  funding?: IDependentValue<
    {
      fundingBody: string;
      grantNumber: string;
    }[]
  >;
  fundingGeneric?: IDependentFundingGenericValue<
    {
      fundingBody: string;
      grantNumber: string;
    }[]
  >;
  LicensesAndPermissions?: string;
  LicensesAndPermissionsReproducedContent?: string;
  coauthorsAgreeToEthics?: boolean;
  coauthorsAgreeToSharing?: boolean;
  coauthorManuscriptOriginalityConfirmed?: boolean;
  coauthorManuscriptNotUnderConsiderationElsewhere?: boolean;
  coauthorAuthorshipAndConflictDeclaration?: boolean;
  coauthorResearchEthicsComplianceConfirmed?: boolean;
  coauthorExportControlComplianceConfirmed?: boolean;
  submissionAgreementConfirmed?: boolean;
  publicationConsentConfirmed?: boolean;
  privacyPolicyConsentConfirmed?: boolean;
  previouslySubmitted?: IDependentValue<string>;
  conflictOfInterestForAuthor?: IDependentValue<string>;
  aiTools?: IDependentValueWithText<string[]>;
  preprint?: IDependentValue<string>;
  manuscriptOwnership?: boolean;
  apcAgreement?: boolean;
  openAccessPublishingConsent?: boolean;
  blindedReviewConsent?: boolean;
  authorDeclaration?: boolean;
  extractionSkipCheckbox?: boolean;
  blindedPeerReviewConsent?: boolean;
  articleSubtype?: string;
  articleLanguage?: string;
  subtitle?: string;
  englishSubtitle?: string;
  ethicsChecklist?: boolean;
  sharedDataset?: IDependentValue<string>;
  generatedDataset?: boolean;
  utilizedDataset?: boolean;
  overlappingMaterial?: boolean;
  ethicalApproval?: IDependentValue<string>;
  ethicsApproval?: IDependentValue<string>;
};

export type ISubmissionFormData = IKeywordsFormData &
  IEnglishKeywordsFormData & {
    type: string;
    metadata: ISubmissionMetadata;
    submittingAuthor: string;
    correspondingAuthor?: string;
    contactAuthor?: string;
    fileSet: ISubmissionFileSet;
    preference: ISubmissionPreference;
    draftSubmissionId?: string;
    hasRunExtraction?: boolean;
    isFileUpdated?: boolean;
  } & ISubmissionContext;

const defaultState: ISubmissionFormData = {
  type: '',
  metadata: {
    title: '',
    englishTitle: '',
    abstract: '',
    englishAbstract: '',
    keywords: [],
    englishKeywords: [],
    authors: [],
    ethicsStatement: undefined,
    clinicalTrials: undefined,
    noveltyStatement: undefined,
    funding: undefined
  },
  submittingAuthor: '',
  isFileUpdated: false,
  correspondingAuthor: undefined,
  contactAuthor: undefined,
  keywords: [],
  englishKeywords: [],
  fileSet: {
    Manuscript: {
      referenceName: 'Manuscript',
      name: '',
      extension: '',
      url: ''
    },
    TitlePage: {
      referenceName: 'TitlePage',
      name: '',
      extension: '',
      url: ''
    }
  },
  preference: {
    dataAvailability: undefined,
    immediatePublication: undefined,
    transparentPeerReview: undefined,
    doubleAnonymousPeerReview: undefined,
    publishingModel: undefined,
    recommendedReviewers: undefined,
    opposedReviewers: undefined,
    dataAvailabilityOpenlyAvailableUrl: undefined,
    dataAvailabilityEmbargoUrl: undefined,
    dataAvailabilityEmbargoDate: undefined,
    dataUnavailabilityReason: undefined
  },
  submissionContext: {
    submissionContextId: '',
    organizationId: '',
    articleWorkflowId: '',
    articleTypeCategoryId: '',
    schemaId: '',
    schema: {
      id: '',
      organizationId: '',
      schema: ''
    },
    documentType: ''
  }
};

const FormContext = createContext<{
  formData: ISubmissionFormData;
  updateFormData: (data: Partial<ISubmissionFormData>) => void;
  resetFormData: () => void;
  consolidatedPdfSourceFileSignature: string | null;
  setConsolidatedPdfSourceFileSignature: (signature: string | null) => void;
}>({
  formData: defaultState,
  updateFormData: () => {},
  resetFormData: () => {},
  consolidatedPdfSourceFileSignature: null,
  setConsolidatedPdfSourceFileSignature: () => {}
});

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }

  return context;
};

export const FormProvider = ({ children }: { children: ReactNode }) => {
  const [formData, setFormData] = useState<ISubmissionFormData>(defaultState);
  const [
    consolidatedPdfSourceFileSignature,
    setConsolidatedPdfSourceFileSignature
  ] = useState<string | null>(null);

  const resetFormData = () => {
    setFormData(defaultState);
    setConsolidatedPdfSourceFileSignature(null);
  };

  const updateFormData = (updatedData: any) => {
    setFormData(prevData => ({
      ...prevData,
      ...omit(updatedData, 'showDiscardChangesModal')
    }));
  };

  return (
    <FormContext.Provider
      value={{
        formData,
        updateFormData,
        resetFormData,
        consolidatedPdfSourceFileSignature,
        setConsolidatedPdfSourceFileSignature
      }}
    >
      {children}
    </FormContext.Provider>
  );
};
